import React from 'react';
import { useLocation } from 'react-router-dom';
import { SponsorBanner } from '@/components/SponsorBanner';

/**
 * Maps the current router path to the placement key the admin selected
 * when configuring a banner. Kept in lock-step with PLACEMENT_OPTIONS in
 * AdminBannersTab.js — same identifiers, same ordering of specificity
 * (detail pages must be tested BEFORE their parent list).
 *
 * Returns null for routes that aren't inner pages (login, signup,
 * profile editor, etc.) so the footer banner stays out of auth & setup
 * flows where it would be intrusive.
 */
function placementForPath(pathname) {
  if (!pathname) return null;
  // Detail-first matches. News articles use a per-article key so admins
  // can attach a bespoke banner to a single story; the backend hierarchy
  // (article → page-wide news-article → app) handles fallbacks.
  const newsMatch = pathname.match(/^\/news\/([^/]+)/);
  if (newsMatch)                           return `news-article:${newsMatch[1]}`;
  if (/^\/events\/[^/]+/.test(pathname))   return 'events-descriptor';
  if (/^\/courses\/[^/]+/.test(pathname))  return 'courses-descriptor';
  if (/^\/britelabs\//.test(pathname))     return 'britelab';
  // List / index pages
  if (pathname === '/progress' || pathname === '/dashboard') return 'progress';
  if (pathname === '/events')              return 'enablers-events';
  if (pathname === '/courses')             return 'enablers-courses';
  if (pathname === '/keynotes')            return 'keynotes';
  if (pathname === '/britecast')           return 'britecast';
  if (pathname === '/learning' || pathname === '/research') return 'learning';
  if (pathname === '/news')                return 'news';
  if (pathname === '/companies')           return 'companies';
  if (pathname === '/jobs')                return 'jobs';
  if (pathname === '/forum')               return 'forum';
  if (pathname === '/britelabs')           return 'britelab';
  if (pathname === '/members')             return 'members';
  if (pathname === '/partners')            return 'partners';
  return null;
}

/**
 * PageFooterBanner — renders the admin-configured "footer" banner at
 * the bottom of every inner page, with the same wide-banner shape as
 * the top banner. Per-page placement overrides the app-wide footer if
 * both are configured (the page-override logic lives in the backend
 * /api/banners?placement=… endpoint).
 */
export default function PageFooterBanner() {
  const { pathname } = useLocation();
  const placement = placementForPath(pathname);
  if (!placement) return null;
  return (
    <div className="container mx-auto max-w-6xl px-3 md:px-6 pb-6" data-testid={`footer-banner-${placement}`}>
      <SponsorBanner placement={placement} type="footer" className="flex justify-center" />
    </div>
  );
}
